/**
 * Per-environment server addresses — ONE file, identical on `staging` and `master`.
 *
 * config.js used to hardcode BASE_URL / WS_LINK, with different values on each
 * branch (master = prod, staging = staging). Any merge or force-push between the
 * two then shipped the wrong backend. Now the addresses of every environment
 * live here and the environment is picked at build/run time, so the same commit
 * is correct on both DigitalOcean apps.
 *
 * Resolution order (first match wins) — see resolveEnvironment():
 *   1. VITE_ODS_BASE_URL / VITE_ODS_WS_LINK  explicit override of one address
 *   2. VITE_ODS_ENV                          'production' | 'staging' | 'local'
 *   3. VITE_SENTRY_ENVIRONMENT               already set on both DO apps
 *   4. `vite --mode staging|local|localtest` (dev server only)
 *   5. the page's hostname at runtime        (HOSTS below)
 *   6. otherwise                             production
 *
 * The legacy VITE_BASE_URL / VITE_API_URL / VITE_WS_LINK are deliberately NOT
 * read: on the DO apps they still hold the old droplet addresses.
 *
 * Pure module (no import.meta, no window): config.js feeds it, and
 * scripts/ci/check-config.mjs imports it to verify the table in CI.
 */

export const ENVIRONMENTS = {
  production: {
    BASE_URL: 'https://prod.api.ordering-dashboard-solution.com/api/v1',
    WS_LINK: 'https://ws.prod.ordering-dashboard-solution.com',
  },
  staging: {
    BASE_URL: 'https://staging.api.ordering-dashboard-solution.com/api/v1',
    WS_LINK: 'https://ws.staging.ordering-dashboard-solution.com',
  },
  local: {
    BASE_URL: 'http://localhost:8000/api/v1',
    WS_LINK: 'http://localhost:1337',
  },
};

/** Hostnames of the admin apps (DO app specs, read 2026-09-30). */
export const HOSTS = {
  production: [
    'console.ordering-dashboard-solution.com',
    'sea-lion-app-fxrx6.ondigitalocean.app', // ods-front-end-admin default ingress
  ],
  staging: [
    'staging.console.ordering-dashboard-solution.com',
    'ods-front-end-admin-staging-87xmr.ondigitalocean.app', // ods-front-end-admin-staging
  ],
};

const ALIASES = {
  production: 'production', prod: 'production',
  staging: 'staging', stage: 'staging',
  local: 'local', localtest: 'local', development: 'local', dev: 'local',
};

/** Map a free-form environment name to production | staging | local (or null). */
export function normalizeEnvironment(name) {
  if (!name) return null;
  return ALIASES[String(name).trim().toLowerCase()] || null;
}

const LOCAL_HOST = /^(localhost|127\.\d+\.\d+\.\d+|0\.0\.0\.0|\[?::1\]?|10\.\d+\.\d+\.\d+|192\.168\.\d+\.\d+|172\.(1[6-9]|2\d|3[01])\.\d+\.\d+|.*\.local)$/i;

/** Environment implied by a hostname, or null when it says nothing. */
export function environmentFromHostname(hostname) {
  const host = String(hostname || '').trim().toLowerCase();
  if (!host) return null;
  if (HOSTS.production.includes(host)) return 'production';
  if (HOSTS.staging.includes(host)) return 'staging';
  if (LOCAL_HOST.test(host)) return 'local';
  // Any other staging.* / *-staging-* host (new DO ingress, preview domain).
  if (/(^|[.-])staging([.-]|$)/.test(host)) return 'staging';
  return null;
}

/**
 * @param {object} p
 * @param {Record<string, string|undefined>} p.env  build-time variables (import.meta.env)
 * @param {string} [p.hostname]                     window.location.hostname, if any
 * @returns {{ env: 'production'|'staging'|'local', source: string, BASE_URL: string, WS_LINK: string }}
 */
export function resolveEnvironment({ env = {}, hostname = '' } = {}) {
  let name = null;
  let source = 'default';
  const pick = (value, from) => {
    if (name) return;
    const n = normalizeEnvironment(value);
    if (n) { name = n; source = from; }
  };
  pick(env.VITE_ODS_ENV, 'VITE_ODS_ENV');
  pick(env.VITE_SENTRY_ENVIRONMENT, 'VITE_SENTRY_ENVIRONMENT');
  // `vite --mode X` on the dev server. DO builds always run in mode
  // "production" for BOTH apps, so the mode is only trusted with the dev server.
  if (env.DEV) pick(env.MODE, 'MODE');
  if (!name) {
    const fromHost = environmentFromHostname(hostname);
    if (fromHost) { name = fromHost; source = 'hostname'; }
  }
  if (!name) name = 'production';

  const base = ENVIRONMENTS[name];
  return {
    env: name,
    source,
    BASE_URL: env.VITE_ODS_BASE_URL || base.BASE_URL,
    WS_LINK: env.VITE_ODS_WS_LINK || base.WS_LINK,
  };
}
